import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  handleMouseWheel,
  SurfaceContext,
  VRContext,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import {
  applyScreenPolygonLinearIndices,
  filterActiveMaskScreenPolygonLinearIndices,
  getScreenPolygonLinearIndices,
} from './Modification';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import {
  dist2,
  finalizePolygon,
  getVisibleActorsDisplayBounds,
  getVtkDisplaySize,
  getWorldUnitsPerPixel,
  polygonToDisplayPoints,
  polylinePath,
  Pt,
  resizeSnapshotPath,
  ZoomSnapshot,
} from './PolygonUtils';
import { ToolId } from 'config/toolDefinitionsConfig';
import { useTool } from '@hooks/Tool/useTool';
import { configStore } from 'config/configStore';
import {
  createMorphSelectionEdit,
  ManualSelectionMorphologyOperation,
} from './manualSelectionMorphology';
import useModalActions from '@hooks/common/useModalAction';

export type SvgDrawLineHandle = {
  cancelInteraction: () => void;
  clear: () => void;
  commitFillPath: () => Promise<void>;
  morphSelection: (
    operation: ManualSelectionMorphologyOperation,
  ) => Promise<void>;
};

type SvgDrawLineProps = {
  context: SurfaceContext | VRContext;
  containerRef: React.RefObject<HTMLDivElement>;
  renderer?: any;
  lowQuality: boolean;
  activeToolId: ToolId | undefined;
};

const isLeftButton = (e: React.PointerEvent) => e.button === 0;

const SvgDrawLine = React.forwardRef<SvgDrawLineHandle, SvgDrawLineProps>(
  function SvgDrawLine(
    { context, containerRef, renderer, lowQuality, activeToolId },
    ref,
  ) {
    const { handleToolActivate } = useTool();
    const { waitingAction, closeLoadingModal } = useModalActions();
    const containerElement = containerRef?.current;
    const rasterCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const isCommittingRef = useRef(false);
    const hasMinimumLength = (arr: unknown[], min = 2) => arr.length > min;
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const dims = containerElement?.getBoundingClientRect() ?? {
      width: 0,
      height: 0,
    };
    const lastToolRef = useRef<ToolId | undefined>('Rotation');

    const [debugClipBounds, setDebugClipBounds] = useState<{
      minX: number;
      minY: number;
      maxX: number;
      maxY: number;
    } | null>(null);

    const isCtrlKeyDownRef = useRef(false);
    const isShiftKeyDownRef = useRef(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [freehand, setFreehand] = useState<Pt[]>([]);
    const [fillPath, setFillPath] = useState<Pt[]>([]);
    const zoomSnapshotRef = useRef<ZoomSnapshot | null>(null);
    const [cursor, setCursor] = useState<Pt | null>(null);
    const { selectedSeriesId } = useBrowserPage();
    const { analysisType } = usePanelContainer();

    const { y: wuppY } = getWorldUnitsPerPixel(renderer);

    const brushWorldRadius = 1;

    const brushRadiusPx = brushWorldRadius / (wuppY || 1e-6);

    const minPxDist = Math.max(1, brushRadiusPx * 0.3);

    const [, setVer] = useState(0);

    const waitForNextPaint = useCallback(
      () =>
        new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        }),
      [],
    );

    const clear = useCallback(() => {
      setFillPath([]);
      setFreehand([]);
      setCursor(null);
      configStore.setData({ manual3DApply: false });
      zoomSnapshotRef.current = null;
    }, []);

    const cancelInteraction = useCallback(() => {
      const current = context as any;
      if (!current) return;
      current.dragState = {
        isDragging: false,
        dragIndex: -1,
        actorPickState: undefined,
      };
      current.lastMousePosition = null;
    }, [context]);


    const getSvgSize = useCallback(() => {
      const rect = containerRef.current?.getBoundingClientRect();
      const w = rect?.width && rect.width > 0 ? rect.width : dims.width;
      const h = rect?.height && rect.height > 0 ? rect.height : dims.height;
      return { w, h };
    }, [containerRef, dims.width, dims.height]);

    const rasterizeForCommit = useCallback(
      (poly: Pt[]) => {
        const { w, h } = getSvgSize();
        const svgSize = { x: w, y: h };

        const displaySize = getVtkDisplaySize(context, renderer, svgSize);

        const sx = displaySize.x / (svgSize.x || 1);
        const sy = displaySize.y / (svgSize.y || 1);
        if (!rasterCanvasRef.current) {
          rasterCanvasRef.current = document.createElement('canvas');
        }
        const rawClipBounds = getVisibleActorsDisplayBounds(
          context,
          renderer,
          80,
        );
        const clipBounds = rawClipBounds
          ? {
              minX: rawClipBounds.minX,
              maxX: rawClipBounds.maxX,
              minY: displaySize.y - rawClipBounds.maxY,
              maxY: displaySize.y - rawClipBounds.minY,
            }
          : undefined;

        setDebugClipBounds(
          clipBounds
            ? {
                minX: clipBounds.minX / sx,
                maxX: clipBounds.maxX / sx,
                minY: clipBounds.minY / sy,
                maxY: clipBounds.maxY / sy,
              }
            : null,
        );

        return polygonToDisplayPoints(
          rasterCanvasRef.current,
          poly,
          displaySize.x,
          displaySize.y,
          renderer,
          selectedSeriesId,
          {
            flipY: true,
            scaleX: sx,
            scaleY: sy,
            clipBounds,
          },
        );
      },
      [context, getSvgSize, renderer, selectedSeriesId],
    );

    useEffect(() => {
      if (activeToolId && activeToolId !== 'Eraser') {
        lastToolRef.current = activeToolId;
      }
    }, [activeToolId]);

    const getCurrentSelectionLinearIndices = useCallback(async () => {
      if (!hasMinimumLength(fillPath)) {
        return null;
      }

      console.time('rasterizeForCommit');
      const commitPath = rasterizeForCommit(fillPath);
      console.timeEnd('rasterizeForCommit');

      if (context.viewportId !== 'Surface3D') {
        return null;
      }

      return getScreenPolygonLinearIndices(
        context as SurfaceContext,
        selectedSeriesId,
        commitPath,
        lowQuality,
        false,
      );
    }, [context, fillPath, lowQuality, rasterizeForCommit, selectedSeriesId]);

    const commitFillPath = useCallback(async () => {
      if (isCommittingRef.current) return;
      console.time('TOTAL_COMMIT');

      if (!hasMinimumLength(fillPath)) {
        console.timeEnd('TOTAL_COMMIT');
        return;
      }

      isCommittingRef.current = true;
      waitingAction({ message: 'Applying' });

      try {
        await waitForNextPaint();

        console.log('fillPath.length', fillPath.length);

        if (context.viewportId === 'Surface3D') {
          const selection = await getCurrentSelectionLinearIndices();

          if (selection?.linearIndices.length) {
            applyScreenPolygonLinearIndices(
              context as SurfaceContext,
              selection.linearIndices,
              0,
            );
          }
        }

        configStore.setData({ manual3DApply: false });
        clear();
      } finally {
        isCommittingRef.current = false;
        closeLoadingModal();
        console.timeEnd('TOTAL_COMMIT');
      }
    }, [
      clear,
      closeLoadingModal,
      context,
      fillPath,
      getCurrentSelectionLinearIndices,
      waitForNextPaint,
      waitingAction,
    ]);

    const morphSelection = useCallback(
      async (operation: ManualSelectionMorphologyOperation) => {
        if (isCommittingRef.current) return;
        if (context.viewportId !== 'Surface3D') return;
        if (!hasMinimumLength(fillPath)) return;

        isCommittingRef.current = true;
        waitingAction({ message: 'Applying' });

        try {
          await waitForNextPaint();

          const selection = await getCurrentSelectionLinearIndices();
          if (!selection) return;

          const seedLinearIndices = filterActiveMaskScreenPolygonLinearIndices(
            context as SurfaceContext,
            selection.linearIndices,
          );
          const edit = createMorphSelectionEdit(
            seedLinearIndices,
            selection.dimensions,
            operation,
          );

          if (edit.linearIndices.length) {
            applyScreenPolygonLinearIndices(
              context as SurfaceContext,
              edit.linearIndices,
              edit.segmentIndex,
            );
          }

          configStore.setData({ manual3DApply: false });
          clear();
        } finally {
          isCommittingRef.current = false;
          closeLoadingModal();
        }
      },
      [
        clear,
        closeLoadingModal,
        context,
        fillPath,
        getCurrentSelectionLinearIndices,
        waitForNextPaint,
        waitingAction,
      ],
    );

    useImperativeHandle(
      ref,
      () => ({ cancelInteraction, clear, commitFillPath, morphSelection }),
      [clear, commitFillPath, morphSelection, cancelInteraction],
    );

    const setZoomSnapshot = useCallback(
      (fill: Pt[]) => {
        const { w, h } = getSvgSize();
        zoomSnapshotRef.current = {
          center: { x: w / 2, y: h / 2 },
          size: { x: w, y: h },
          fill,
        };
      },
      [getSvgSize],
    );

    useEffect(() => {
      if (!renderer) return;
      const cam = renderer.getActiveCamera?.();
      const renderSnapshot = () => {
        const snapshot = zoomSnapshotRef.current;
        if (!snapshot) return;
        const { w, h } = getSvgSize();
        const currentSize = { x: w, y: h };
        const resized = resizeSnapshotPath(snapshot, currentSize);
        setFillPath(resized.path);
      };
      const handleCameraModified = () => {
        setVer((v) => v + 1);
        renderSnapshot();
      };
      const handleResize = () => {
        setVer((v) => v + 1);
        renderSnapshot();
      };
      const cameraSubscription = cam?.onModified?.(handleCameraModified);
      const ro = new ResizeObserver(handleResize);
      containerRef.current && ro.observe(containerRef.current);
      return () => {
        cam?.off?.(handleCameraModified);
        cameraSubscription?.unsubscribe?.();
        ro.disconnect();
      };
    }, [renderer, containerRef, getSvgSize]);

    const clientToSvg = useCallback(
      (clientX: number, clientY: number): Pt | null => {
        const svg = svgRef.current;
        if (!svg) return null;
        const pt = svg.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const ctm = svg.getScreenCTM();
        if (!ctm) return null;
        const inv = ctm.inverse();
        const sp = pt.matrixTransform(inv);
        return { x: sp.x, y: sp.y };
      },
      [],
    );

    const pushFreehand = useCallback(
      (p: Pt) => {
        setFreehand((prev) => {
          if (prev.length === 0) return [p];
          const last = prev[prev.length - 1];
          if (dist2(last, p) >= minPxDist * minPxDist) return [...prev, p];
          return prev;
        });
      },
      [minPxDist],
    );

    const onPointerDown = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        overlayRef.current?.focus();
        isCtrlKeyDownRef.current = e.ctrlKey;
        isShiftKeyDownRef.current = configStore.get().manual3DTool.eraser;        if (isShiftKeyDownRef.current && isLeftButton(e)) {
          handleToolActivate?.('Eraser');
          setFreehand([]);
          setFillPath([]);
          zoomSnapshotRef.current = null;
          const p = clientToSvg(e.clientX, e.clientY);
          if (!p) return;
          setIsDrawing(true);
          setFreehand([p]);
          configStore.setData({manual3DApply :false});
          (e.target as Element).setPointerCapture?.(e.pointerId);
        } else {
          setCursor(null);
          handleMouseDown(
            { current: context } as React.MutableRefObject<SurfaceContext>,
            e.nativeEvent,
            analysisType,
          );
          return;
        }
      },
      [analysisType, clientToSvg, context, fillPath.length],
    );

    const lastPtRef = useRef<Pt | null>(null);
    const tickingRef = useRef(false);

    const onPointerMove = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        const wasShiftDownOnPointerDown = isShiftKeyDownRef.current;

        if (wasShiftDownOnPointerDown && (e.buttons & 1) !== 0) {
          const p = clientToSvg(e.clientX, e.clientY);
          if (!p) return;
          setCursor(p);

          if (!isDrawing) return;
          lastPtRef.current = p;
          if (tickingRef.current) return;
          tickingRef.current = true;
          requestAnimationFrame(() => {
            tickingRef.current = false;
            if (lastPtRef.current) pushFreehand(lastPtRef.current);
          });
        } else{
          setCursor(null);
          handleMouseMove({
            context: {
              current: context,
            } as React.MutableRefObject<SurfaceContext>,
            evt: e.nativeEvent,
            toolName: activeToolId,
            analysisType,
          });
        }
      },
      [
        analysisType,
        clientToSvg,
        context,
        fillPath.length,
        isDrawing,
        pushFreehand,
      ],
    );

    const onPointerUp = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        const wasCtrlDownOnPointerDown = isCtrlKeyDownRef.current;
        const wasShiftDownOnPointerDown = isShiftKeyDownRef.current;

        isCtrlKeyDownRef.current = false;
        isShiftKeyDownRef.current = false;

        if (wasShiftDownOnPointerDown && isLeftButton(e)) {
          if (!isDrawing) return;
          if (lastToolRef.current && activeToolId === 'Eraser') {
            handleToolActivate?.(lastToolRef.current);
          }
          (e.target as Element).releasePointerCapture?.(e.pointerId);
          setIsDrawing(false);

          const merged = [...freehand];
          const cleaned = finalizePolygon(merged, {
            dedup: minPxDist,
            snap: 1,
            useHull: false,
          });

          // Dense fill pixels are built only when committing.
          setFillPath(cleaned);
          configStore.setData({manual3DApply :true});
          setZoomSnapshot(cleaned);
          setFreehand([]);
        } else {
          setCursor(null);
          handleMouseUp(
            { current: context } as React.MutableRefObject<SurfaceContext>,
            e.nativeEvent,
            analysisType,
            selectedSeriesId,
          );
        }
      },
      [
        analysisType,
        context,
        fillPath.length,
        isDrawing,
        freehand,
        minPxDist,
        setZoomSnapshot,
      ],
    );

    if (!containerElement) return null;

    return (
      <div
        ref={overlayRef}
        tabIndex={0}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'auto',
          touchAction: 'none',
          outline: 'none',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={() => setCursor(null)}
        onWheel={(evt) =>
          handleMouseWheel(
            { current: context } as React.MutableRefObject<SurfaceContext>,
            evt.nativeEvent,
          )
        }
      >
        <svg
          ref={svgRef}
          width={dims.width}
          height={dims.height}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            touchAction: 'none',
          }}
        >
          {cursor && !hasMinimumLength(fillPath) && (
            <circle
              cx={cursor.x}
              cy={cursor.y}
              r={brushRadiusPx / 2}
              fill="rgba(255,0,0,0.15)"
              stroke="#ff0000"
              strokeWidth={1}
              pointerEvents="none"
            />
          )}

          {fillPath.length > 1 && (
            <path
              d={polylinePath(fillPath, true)}
              fill="rgba(255,0,0,0.15)"
              stroke="#ff4040"
              strokeWidth={1}
              strokeLinejoin="round"
              strokeLinecap="round"
              pointerEvents="none"
              opacity={0.7}
            />
          )}

          {freehand.length > 1 && (
            <path
              d={polylinePath(freehand, false)}
              fill="none"
              stroke="#fa0000"
              strokeWidth={1}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          {/* <text x="50%" y="5%" textAnchor="middle" fill="#9CA3AF" fontSize="12">
            <tspan x="50%" dy="0">
              Left-click and drag to draw
            </tspan>
            <tspan x="50%" dy="16">
              Press Esc to clear the selection area
            </tspan>
            <tspan x="50%" dy="16">
              Press Enter to crop
            </tspan>
          </text> */}
        </svg>
      </div>
    );
  },
);

export default SvgDrawLine;
